"use client";

import { IconChevronDownOutline18, IconChevronUpOutline18, IconCreditCardOutline18, IconShopOutline18 } from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useX402Tokens } from "@/hooks/useX402Tokens";
import * as React from "react";
import AddressInput from "./AddressInput";
import ArrayInput from "./ArrayInput";
import ChainSelector from "./ChainSelector";
import InputSchemaBuilder, { fieldsToJsonSchema, jsonSchemaToFields, SchemaField } from "./InputSchemaBuilder";
import SelectInput from "./SelectInput";
import X402TokenSelector from "./X402TokenSelector";
interface X402Config {
  price?: string;
  token?: string;
  chainId?: number;
  recipient?: string;
  discoverable?: boolean;
  discoveryMetadata?: {
    name?: string;
    description?: string;
    category?: string;
    tags?: string[];
    documentation?: string;
    logo?: string;
    provider?: string;
  };
  discoveryInput?: {
    example?: any;
    schema?: Record<string, any>;
  };
  discoveryOutput?: {
    example?: any;
    schema?: Record<string, any>;
  };
}
interface X402ConfigInputProps {
  value: X402Config | undefined;
  onChange: (value: X402Config | undefined) => void;
}
const CATEGORY_OPTIONS = ["data", "automation", "ai", "defi", "messaging", "analytics", "other"];
function X402ConfigInput({
  value,
  onChange
}: X402ConfigInputProps) {
  const [isEnabled, setIsEnabled] = React.useState(!!value && Object.keys(value).length > 0);
  const [tokenDecimals, setTokenDecimals] = React.useState<number>(6); // Default to USDC decimals
  const [showDiscoveryMetadata, setShowDiscoveryMetadata] = React.useState(false);

  // Price display state (human-readable)
  const [priceDisplay, setPriceDisplay] = React.useState<string>("");

  // Use hook to fetch token metadata for existing configs
  const {
    fetchTokenMetadata
  } = useX402Tokens(value?.chainId);

  // Fetch token decimals when loading existing config
  const lastFetchedTokenRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (value?.token && value?.chainId && lastFetchedTokenRef.current !== value.token) {
      lastFetchedTokenRef.current = value.token;
      fetchTokenMetadata(value.chainId, value.token).then(token => {
        if (token) {
          setTokenDecimals(token.decimals);
        }
      });
    }
  }, [value?.token, value?.chainId, fetchTokenMetadata]);

  // Sync isEnabled when value changes externally (e.g., navigating back to node)
  // Only sync if value has meaningful content (not just an empty object from initial enable)
  const lastValueRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    const currentValueStr = JSON.stringify(value);
    // Skip if this is the same value we just set
    if (currentValueStr === lastValueRef.current) {
      return;
    }
    lastValueRef.current = currentValueStr;

    // Check if value has meaningful content (more than just empty object)
    const hasMeaningfulContent = !!value && (value.chainId !== undefined || value.token !== undefined || value.price !== undefined || value.recipient !== undefined);
    if (hasMeaningfulContent && !isEnabled) {
      setIsEnabled(true);
    }
  }, [value, isEnabled]);

  // Sync price display when value changes
  React.useEffect(() => {
    if (value?.price && tokenDecimals) {
      const rawPrice = BigInt(value.price);
      const divisor = BigInt(10 ** tokenDecimals);
      const wholePart = rawPrice / divisor;
      const fractionalPart = rawPrice % divisor;
      const fractionalStr = fractionalPart.toString().padStart(tokenDecimals, "0");
      // Trim trailing zeros
      const trimmedFractional = fractionalStr.replace(/0+$/, "");
      if (trimmedFractional) {
        setPriceDisplay(`${wholePart}.${trimmedFractional}`);
      } else {
        setPriceDisplay(wholePart.toString());
      }
    }
  }, [value?.price, tokenDecimals]);
  const handleToggle = () => {
    if (isEnabled) {
      // Disable - clear config
      setIsEnabled(false);
      onChange(undefined);
    } else {
      // Enable - initialize with empty config
      setIsEnabled(true);
      onChange({});
    }
  };
  const updateConfig = (updates: Partial<X402Config>) => {
    const newConfig = {
      ...value,
      ...updates
    };
    onChange(newConfig);
  };
  const updateDiscoveryMetadata = (updates_0: Partial<NonNullable<X402Config["discoveryMetadata"]>>) => {
    const currentMetadata = value?.discoveryMetadata || {};
    const newMetadata = {
      ...currentMetadata,
      ...updates_0
    };
    // Clean up empty values
    Object.keys(newMetadata).forEach(key => {
      const k = key as keyof typeof newMetadata;
      if (newMetadata[k] === "" || Array.isArray(newMetadata[k]) && (newMetadata[k] as any[]).length === 0) {
        delete newMetadata[k];
      }
    });
    updateConfig({
      discoveryMetadata: Object.keys(newMetadata).length > 0 ? newMetadata : undefined
    });
  };
  const handleChainChange = (chainId: number | string) => {
    // Reset token when chain changes
    // In x402 mode, we always get numeric chainIds
    const numericChainId = typeof chainId === "number" ? chainId : parseInt(chainId, 10);
    updateConfig({
      chainId: numericChainId,
      token: undefined
    });
  };
  const handleTokenChange = (tokenAddress: string, decimals: number) => {
    setTokenDecimals(decimals);
    updateConfig({
      token: tokenAddress
    });
  };
  const handlePriceChange = (displayPrice: string) => {
    setPriceDisplay(displayPrice);

    // Convert to smallest unit
    if (!displayPrice || displayPrice === "") {
      updateConfig({
        price: undefined
      });
      return;
    }
    try {
      // Parse the display price (e.g., "1.5" -> 1500000 for 6 decimals)
      const parts = displayPrice.split(".");
      const wholePart_0 = parts[0] || "0";
      let fractionalPart_0 = parts[1] || "";

      // Pad or trim fractional part to match decimals
      if (fractionalPart_0.length > tokenDecimals) {
        fractionalPart_0 = fractionalPart_0.slice(0, tokenDecimals);
      } else {
        fractionalPart_0 = fractionalPart_0.padEnd(tokenDecimals, "0");
      }
      const rawPrice_0 = BigInt(wholePart_0) * BigInt(10 ** tokenDecimals) + BigInt(fractionalPart_0);
      updateConfig({
        price: rawPrice_0.toString()
      });
    } catch {
      // Invalid input, don't update
    }
  };
  return <div className="space-y-4">
      {/* Enable Toggle */}
      <div className="border-ds-border-default dark:bg-ds-neutral-800/50 rounded-lg border bg-zinc-50 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IconCreditCardOutline18 className="text-ds-text-subtle dark:text-ds-text-disabled h-5 w-5" />
            <div>
              <label className="field-label block">Enable x402 Payment Gating</label>
              <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">
                Require payment before webhook triggers
              </p>
            </div>
          </div>
          <button type="button" role="switch" aria-checked={isEnabled} onClick={handleToggle} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isEnabled ? "bg-blue-600" : "bg-ds-neutral-300 dark:bg-zinc-600"}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isEnabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
      </div>
      {/* Payment Configuration */}
      {isEnabled && <div className="border-ds-border-default space-y-4 rounded-lg border p-4">
          <h4 className="body-sm-semibold">Payment Configuration</h4>

          {/* Chain */}
          <ChainSelector label="Receive Chain" description="Chain where you want to receive payment" value={value?.chainId} onChange={handleChainChange} x402 />

          {/* Token */}
          <X402TokenSelector label="Receive Token" description="Token you want to receive (payers can pay from any chain)" value={value?.token} chainId={value?.chainId} onChange={handleTokenChange} required allowCustom={false} />

          {/* Price */}
          <div className="space-y-2">
            <label className="field-label block">
              Price <span className="text-red-500">*</span>
            </label>
            <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">
              Amount to charge per trigger (in token units)
            </p>
            <div className="relative">
              <Input type="text" value={priceDisplay} onChange={e => handlePriceChange(e.target.value)} placeholder="0.00" className="text-ds-text-primary" />
            </div>
            {value?.price && <p className="label-xs">
                Raw value: {value.price} ({tokenDecimals} decimals)
              </p>}
          </div>

          {/* Recipient */}
          <AddressInput label="Recipient Address" description="Address that will receive payments" value={value?.recipient || ""} onChange={recipient => updateConfig({
        recipient
      })} required />
        </div>}
      {/* Discovery Configuration */}
      {isEnabled && <div className="border-ds-border-default space-y-4 rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <IconShopOutline18 className="text-ds-text-subtle dark:text-ds-text-disabled h-5 w-5" />
              <div>
                <label className="field-label block">List on Bazaar</label>
                <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">
                  Make this workflow discoverable in the marketplace
                </p>
              </div>
            </div>
            <button type="button" role="switch" aria-checked={value?.discoverable || false} onClick={() => updateConfig({
          discoverable: !value?.discoverable
        })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${value?.discoverable ? "bg-blue-600" : "bg-ds-neutral-300 dark:bg-zinc-600"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value?.discoverable ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>

          {/* Discovery Metadata */}
          {value?.discoverable && <div className="mt-4 space-y-4">
              {/* Collapsible Metadata Section */}
              <button type="button" onClick={() => setShowDiscoveryMetadata(!showDiscoveryMetadata)} className="bg-ds-neutral-100 text-ds-text-primary hover:bg-ds-neutral-200 dark:bg-ds-neutral-800 dark:hover:bg-ds-neutral-700 flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-medium">
                <span>Discovery Metadata</span>
                {showDiscoveryMetadata ? <IconChevronUpOutline18 className="h-4 w-4" /> : <IconChevronDownOutline18 className="h-4 w-4" />}
              </button>

              {showDiscoveryMetadata && <div className="space-y-4 pl-2">
                  {/* Name */}
                  <div className="space-y-2">
                    <label className="field-label block">Display Name</label>
                    <Input type="text" value={value?.discoveryMetadata?.name || ""} onChange={e_0 => updateDiscoveryMetadata({
              name: e_0.target.value
            })} placeholder="My Awesome Workflow" className="text-ds-text-primary" />
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <label className="field-label block">Description</label>
                    <Textarea value={value?.discoveryMetadata?.description || ""} onChange={e_1 => updateDiscoveryMetadata({
              description: e_1.target.value
            })} placeholder="What does this workflow do?" rows={3} className="border-ds-neutral-300 dark:border-ds-neutral-700 w-full rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
                  </div>

                  {/* Category */}
                  <SelectInput label="Category" value={value?.discoveryMetadata?.category || ""} options={CATEGORY_OPTIONS} onChange={category => updateDiscoveryMetadata({
            category
          })} />

                  {/* Tags */}
                  <ArrayInput label="Tags" description="Keywords for search" value={value?.discoveryMetadata?.tags || []} onChange={tags => updateDiscoveryMetadata({
            tags: tags as string[]
          })} />

                  {/* Documentation URL */}
                  <div className="space-y-2">
                    <label className="field-label block">Documentation URL</label>
                    <Input type="text" value={value?.discoveryMetadata?.documentation || ""} onChange={e_2 => updateDiscoveryMetadata({
              documentation: e_2.target.value
            })} placeholder="https://docs.example.com/workflow" className="text-ds-text-primary" />
                  </div>

                  {/* Logo URL */}
                  <div className="space-y-2">
                    <label className="field-label block">Logo URL</label>
                    <Input type="text" value={value?.discoveryMetadata?.logo || ""} onChange={e_3 => updateDiscoveryMetadata({
              logo: e_3.target.value
            })} placeholder="https://example.com/logo.png" className="text-ds-text-primary" />
                  </div>

                  {/* Provider */}
                  <div className="space-y-2">
                    <label className="field-label block">Provider Name</label>
                    <Input type="text" value={value?.discoveryMetadata?.provider || ""} onChange={e_4 => updateDiscoveryMetadata({
              provider: e_4.target.value
            })} placeholder="Your Company Name" className="text-ds-text-primary" />
                  </div>
                </div>}

              {/* Input Schema Builder */}
              <InputSchemaBuilder initialFields={jsonSchemaToFields(value?.discoveryInput?.schema)} onChange={(fields: SchemaField[]) => {
          const schema = fieldsToJsonSchema(fields);
          // Also generate example from fields
          const example: Record<string, any> = {};
          for (const field of fields) {
            if (field.name) {
              if (field.type === "string") example[field.name] = "";else if (field.type === "number") example[field.name] = 0;else if (field.type === "boolean") example[field.name] = false;
            }
          }
          updateConfig({
            discoveryInput: {
              schema,
              example: Object.keys(example).length > 0 ? example : undefined
            }
          });
        }} />
            </div>}
        </div>}
    </div>;
}
export default React.memo(X402ConfigInput);